<template>
  <b-container>
    <KTCodePreview v-bind:title="'Chi tiết Hình ảnh'">
      <template v-slot:preview>
        <div v-if="image">
          <b-row>
            <b-col cols="12" md="6">
              <b-card no-body class="border">
                <div
                  class="image-container bg-light d-flex align-items-center justify-content-center"
                >
                  <b-img
                    :src="image.url"
                    fluid
                    :alt="image.name"
                    class="image-preview"
                  ></b-img>
                  <div v-if="!image.url" class="text-center">
                    <b-spinner
                      variant="primary"
                      label="Đang tải..."
                    ></b-spinner>
                  </div>
                </div>
              </b-card>
            </b-col>

            <b-col cols="12" md="6">
              <b-card no-body class="border">
                <b-card-title class="p-3 border-bottom">Thông tin</b-card-title>

                <b-list-group flush>
                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Tên</small>
                    <span>{{ image.name }}</span>
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Tên gốc</small>
                    <span>{{ image.originalName || 'N/A' }}</span>
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Kích cỡ tập tin</small>
                    <span>{{ formatSize(image.size) }}</span>
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Kích thước</small>
                    <span
                      >{{ image.width || '?' }}x{{ image.height || '?' }}</span
                    >
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Kiểu MIME</small>
                    <span>{{ image.mimeType || 'N/A' }}</span>
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Trạng thái</small>
                    <span>
                      <b-badge :variant="getStatusVariant(image.status)">
                        {{ image.status }}
                      </b-badge>
                    </span>
                  </b-list-group-item>

                  <b-list-group-item class="d-flex flex-column">
                    <small class="text-muted">Tải vào lúc</small>
                    <span>{{ formatDate(image.createdAt) }}</span>
                  </b-list-group-item>
                </b-list-group>

                <b-card-footer class="d-flex justify-content-end bg-white">
                  <b-button
                    variant="outline-primary"
                    class="mr-2"
                    :href="image.url"
                    target="_blank"
                  >
                    <i class="fa fa-external-link-alt mr-1"></i>
                    Mở tập tin gốc
                  </b-button>
                  <b-button variant="danger" @click="confirmDelete">
                    <i class="fa fa-trash mr-1"></i>
                    Xóa
                  </b-button>
                </b-card-footer>
              </b-card>
            </b-col>
          </b-row>

          <b-row v-if="imageUsages && imageUsages.length > 0" class="mt-4">
            <b-col cols="12">
              <b-card class="border">
                <b-card-title>Thông tin sử dụng</b-card-title>

                <b-card-text>
                  <p>
                    Hình ảnh này đang được sử dụng trong
                    {{ imageUsages.length }} vị trí:
                  </p>

                  <b-list-group>
                    <b-list-group-item
                      v-for="usage in imageUsages"
                      :key="usage.id"
                      class="d-flex align-items-center"
                    >
                      <div class="mr-3">
                        <i :class="getModelIconClass(usage.modelName)"></i>
                      </div>

                      <div>
                        {{ getModelLabel(usage.modelName) }}
                        <b-link
                          v-if="usage.modelName === 'product'"
                          :to="`/products/update-product?id=${usage.modelId}`"
                        >
                          (ID: {{ usage.modelId }})
                        </b-link>
                        <span v-else>(ID: {{ usage.modelId }})</span>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </b-card-text>
              </b-card>
            </b-col>
          </b-row>
        </div>
        <!-- <loading-overlay :active="isLoading" /> -->
      </template>
    </KTCodePreview>

    <!-- Delete confirmation dialog -->
    <b-modal
      v-model="deleteDialog"
      title="Xóa Hình ảnh"
      header-bg-variant="light"
      ok-variant="danger"
      ok-title="Xóa"
      cancel-title="Hủy bỏ"
      @ok="deleteImage"
      :ok-disabled="deletingImage"
    >
      <p>Bạn có chắc chắn muốn xóa hình ảnh này không?</p>
      <div
        v-if="imageUsages && imageUsages.length > 0"
        class="text-danger mt-2"
      >
        Cảnh báo: Hình ảnh này đang được sử dụng trong
        {{ imageUsages.length }} vị trí.
      </div>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Hủy bỏ
        </b-button>
        <b-button variant="danger" @click="ok()" :disabled="deletingImage">
          <b-spinner small v-if="deletingImage" class="mr-1"></b-spinner>
          Xóa
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
// import LoadingOverlay from '@/view/components/common/LoadingOverlay';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';

export default {
  name: 'ImageDetail',
  components: {
    // LoadingOverlay,
    KTCodePreview,
  },
  data() {
    return {
      deleteDialog: false,
      deletingImage: false,
    };
  },
  computed: {
    ...mapGetters('images', ['currentImage', 'isLoading', 'imageUsagesById']),

    imageId() {
      return this.$route.params.id;
    },

    image() {
      return this.currentImage;
    },

    imageUsages() {
      return this.imageUsagesById(this.imageId);
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Chi tiết Hình ảnh' }]);
  },
  methods: {
    ...mapActions('images', ['getImageById', 'deleteImage', 'getImageUsages']),

    formatSize(bytes) {
      if (!bytes) return '0 B';

      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024;
        i++;
      }

      return `${bytes.toFixed(1)} ${units[i]}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleString();
    },

    getStatusVariant(status) {
      switch (status) {
        case 'READY':
          return 'success';
        case 'PENDING':
          return 'warning';
        case 'FAILED':
          return 'danger';
        default:
          return 'secondary';
      }
    },

    confirmDelete() {
      this.deleteDialog = true;
    },

    async deleteImage() {
      this.deletingImage = true;

      try {
        await this.deleteImage(this.imageId);
        this.$toast.success('Image deleted successfully');
        this.$router.push('/images');
      } catch (error) {
        console.error('Error deleting image:', error);
        this.$toast.error(`Error deleting image: ${error.message}`);
      } finally {
        this.deleteDialog = false;
        this.deletingImage = false;
      }
    },

    getModelIconClass(modelName) {
      switch (modelName) {
        case 'product':
          return 'fa fa-box';
        case 'user_avatar':
          return 'fa fa-user';
        case 'user_cover':
          return 'fa fa-image';
        default:
          return 'fa fa-file';
      }
    },

    getModelLabel(modelName) {
      switch (modelName) {
        case 'product':
          return 'Product';
        case 'user_avatar':
          return 'User Avatar';
        case 'user_cover':
          return 'User Cover';
        default:
          return modelName;
      }
    },
  },
  created() {
    this.getImageById(this.imageId);
    this.getImageUsages(this.imageId);
  },
};
</script>

<style scoped>
.image-container {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  max-height: 400px;
  max-width: 100%;
  object-fit: contain;
}
</style>
